import {
  AmountType,
  ClientData,
  ConfigurationItem,
  CreateTradeGeneralData,
  CreateTradeRequest,
  DefaultOrder,
  FormTrailingStopData,
  FreeTextTradeData,
  Option,
  OrderTypes,
  PrecisionConfig,
} from "@shared/types";
import {
  ErrorTypeToDisplay,
  InputOrderData,
  OrderData,
  OrderSectionErrors,
  OrdersErrors,
  RemoveOrder,
  SetOrders,
  UpdateOrder,
} from "@shared/features/trades/common/types";
import { Dispatch, SetStateAction } from "react";

export enum TradingTerminalTabs {
  SMART_TRADE = "tabs.smart_trade",
  SIMPLE_ORDER = "tabs.simple_order",
}

export enum CreateTradeSections {
  GENERAL = "sections.general",
  ENTRIES = "sections.entries",
  TAKE_PROFITS = "sections.take_profits",
  STOP = "sections.stop",
}

export interface TradeFormValues {
  account?: ClientData;
  exchanges?: string[];
  subGroupIds?: number[];
  accountChanged: boolean;
  symbols: string[];
  symbolChanged?: boolean;
  amount: string | undefined;
  amountPercent: string | undefined;
  amountType: AmountType | undefined;
  amountInitialized: boolean;
  quantity: string | undefined;
  quantityPercent: string | undefined;
  extraInfo: ExtraInfo | undefined;
  rawFreeText: string | undefined;
  freeTextTradeData: FreeTextTradeData | undefined;
  freeTextProcessed: boolean;
  useFreeTextData: boolean;
  updatedItemsToFreeTextData: Record<OrderTypes, boolean>;
  useTakeProfitOrders: boolean;
  useStopOrders: boolean;
  showRedistributeTargetsButton: Record<OrderTypes, boolean>;
  shouldRedistributePriceTargets: Record<OrderTypes, boolean>;
  priceTargetsModified: Record<OrderTypes, boolean | undefined>;
  strategyValuesModified: Record<OrderTypes, boolean | undefined>;
  isStrategyInitialized: Record<OrderTypes, boolean | undefined>;
  ignoreOrdersRatios: Record<OrderTypes, boolean>;
  showNewCustomStrategy: Record<OrderTypes, boolean | undefined>;
  ratioTargets: Record<OrderTypes, number[] | undefined>;
  useTrailing: Record<OrderTypes, boolean>;
  defaultStop: DefaultOrder<string>;
  entryOrders: InputOrderData[];
  takeProfitOrders: InputOrderData[];
  stopOrders: InputOrderData[];
  priceRangeInitialized: Record<OrderTypes, boolean>;
  existingQuantityOrder: InputOrderData | undefined;
  tabWithErrors: Record<CreateTradeSections, boolean>;
  generalDataProcessed: boolean;
  shouldUpdatePriceRange: Record<OrderTypes, boolean>;
  shouldUpdateNumOfOrders: boolean;
  trailingEntry: number | undefined;
  trailingTakeProfit: number | undefined;
  trailingStop: FormTrailingStopData<number> | undefined;
  stopTimeout: number | undefined;
  prevGeneralOptionsMapping: CreateTradeGeneralData | undefined;
  isTpNumOrdersInitialized: boolean;
  strategyHasFixedNumOrders: Record<OrderTypes, boolean>;
  showedOppositePositionError: boolean;
  valuesModified: boolean;
  ordersInitialized: Record<OrderTypes, boolean>;
  numOrdersChangedManually: Record<OrderTypes, boolean>;
  stopDistanceInputModified: boolean;
  stopBaselineModified: boolean;
}

export interface TradeFormSetters {
  setAccount: Dispatch<SetStateAction<TradeFormValues["account"]>>;
  setExchanges: Dispatch<SetStateAction<TradeFormValues["exchanges"]>>;
  setSubGroupIds: Dispatch<SetStateAction<TradeFormValues["subGroupIds"]>>;
  setAccountChanged: Dispatch<
    SetStateAction<TradeFormValues["accountChanged"]>
  >;
  setSymbols: Dispatch<SetStateAction<TradeFormValues["symbols"]>>;
  setSymbolChanged: Dispatch<SetStateAction<TradeFormValues["symbolChanged"]>>;
  setAmount: Dispatch<SetStateAction<TradeFormValues["amount"]>>;
  setAmountPercent: Dispatch<SetStateAction<TradeFormValues["amountPercent"]>>;
  setAmountType: Dispatch<SetStateAction<TradeFormValues["amountType"]>>;
  setAmountInitialized: Dispatch<
    SetStateAction<TradeFormValues["amountInitialized"]>
  >;
  setQuantity: Dispatch<SetStateAction<TradeFormValues["quantity"]>>;
  setQuantityPercent: Dispatch<
    SetStateAction<TradeFormValues["quantityPercent"]>
  >;
  setExtraInfo: Dispatch<SetStateAction<TradeFormValues["extraInfo"]>>;
  setRawFreeText: Dispatch<SetStateAction<TradeFormValues["rawFreeText"]>>;
  setFreeTextTradeData: Dispatch<
    SetStateAction<TradeFormValues["freeTextTradeData"]>
  >;
  setFreeTextProcessed: Dispatch<
    SetStateAction<TradeFormValues["freeTextProcessed"]>
  >;
  setUseFreeTextData: Dispatch<
    SetStateAction<TradeFormValues["useFreeTextData"]>
  >;
  setUpdatedItemsToFreeTextData: (
    orderType: OrderTypes,
    value: boolean
  ) => void;
  setUseTakeProfitOrders: Dispatch<
    SetStateAction<TradeFormValues["useTakeProfitOrders"]>
  >;
  setUseStopOrders: Dispatch<SetStateAction<TradeFormValues["useStopOrders"]>>;
  setShowRedistributeTargetsButton: (
    orderType: OrderTypes,
    value: boolean
  ) => void;
  setShouldRedistributePriceTargets: (
    orderType: OrderTypes,
    value: boolean
  ) => void;
  setPriceTargetsModified: (orderType: OrderTypes, value: boolean) => void;
  setStrategyValuesModified: (orderType: OrderTypes, value: boolean) => void;
  setIsStrategyInitialized: (orderType: OrderTypes, value: boolean) => void;
  setIgnoreOrdersRatios: (orderType: OrderTypes, value: boolean) => void;
  setShowNewCustomStrategy: (orderType: OrderTypes, value: boolean) => void;
  setRatioTargets: (orderType: OrderTypes, value: number[] | undefined) => void;
  setUseTrailing: (orderType: OrderTypes, value: boolean) => void;
  setDefaultStop: Dispatch<SetStateAction<TradeFormValues["defaultStop"]>>;
  setEntryOrders: Dispatch<SetStateAction<TradeFormValues["entryOrders"]>>;
  setTakeProfitOrders: Dispatch<
    SetStateAction<TradeFormValues["takeProfitOrders"]>
  >;
  setStopOrders: Dispatch<SetStateAction<TradeFormValues["stopOrders"]>>;
  setPriceRangeInitialized: (orderType: OrderTypes, value: boolean) => void;
  setExistingQuantityOrder: Dispatch<
    SetStateAction<TradeFormValues["existingQuantityOrder"]>
  >;
  setTabWithErrors: (tab: CreateTradeSections, value: boolean) => void;
  setGeneralDataProcessed: Dispatch<
    SetStateAction<TradeFormValues["generalDataProcessed"]>
  >;
  setShouldUpdatePriceRange: (orderType: OrderTypes, value: boolean) => void;
  setShouldUpdateNumOfOrders: Dispatch<
    SetStateAction<TradeFormValues["shouldUpdateNumOfOrders"]>
  >;
  setTrailingEntry: Dispatch<SetStateAction<TradeFormValues["trailingEntry"]>>;
  setTrailingTakeProfit: Dispatch<
    SetStateAction<TradeFormValues["trailingTakeProfit"]>
  >;
  setTrailingStop: Dispatch<SetStateAction<TradeFormValues["trailingStop"]>>;
  setStopTimeout: Dispatch<SetStateAction<TradeFormValues["stopTimeout"]>>;
  setPrevGeneralOptionsMapping: Dispatch<
    SetStateAction<TradeFormValues["prevGeneralOptionsMapping"]>
  >;
  setIsTpNumOrdersInitialized: Dispatch<
    SetStateAction<TradeFormValues["isTpNumOrdersInitialized"]>
  >;
  setStrategyHasFixedNumOrders: (orderType: OrderTypes, value: boolean) => void;
  setShowedOppositePositionError: Dispatch<
    SetStateAction<TradeFormValues["showedOppositePositionError"]>
  >;
  setValuesModified: Dispatch<
    SetStateAction<TradeFormValues["valuesModified"]>
  >;
  setOrdersInitialized: (orderType: OrderTypes, value: boolean) => void;
  setNumOrdersChangedManually: (orderType: OrderTypes, value: boolean) => void;
  setStopDistanceInputModified: Dispatch<
    SetStateAction<TradeFormValues["stopDistanceInputModified"]>
  >;
  setStopBaselineModified: Dispatch<
    SetStateAction<TradeFormValues["stopBaselineModified"]>
  >;
}

export interface AmountData {
  precisionConfig: PrecisionConfig | undefined;
  amountCoin: string | undefined;
  quantityCoin: string | undefined;
  availableAmount: number;
  availableUsdAmount: number;
  availableQuantity: number;
  minAmount: number | undefined;
  minQuantity: number;
  quantityAmount: number;
  priceLimits: { min: number; max: number };
  getMinAmountByPrice: (price: number) => number;
  checkOppositePositionError: (reverse: boolean) => string | undefined;
  convertMainCoinAmountToUSD?: (
    mainCoinAmount: number,
    applyPrecision?: boolean
  ) => number | undefined;
  usdAmountPrecision: number;
}

export interface GeneralConfigurationData {
  generalConfiguration: React.JSX.Element | undefined;
  strategy: number[];
  strategyId?: number;
  numOrders: number;
  priceRange: PriceRange;
  itemsModified?: string[];
  setItemsModified: (itemsModified: string[]) => void;
  clonedConfiguration: ConfigurationItem | undefined;
  updateConfiguration: () => void;
  configurationModified: boolean;
  accountId?: number;
}

export enum TradeTypes {
  REGULAR = 1,
  MARKET_BUY = 2,
  MARKET_SELL = 3,
  BREAKOUT = 4,
}

export type PriceRange = {
  min: number;
  max: number;
};

export type ExtraInfo = Pick<
  CreateTradeRequest,
  | "trade_type"
  | "reverse"
  | "margin_type"
  | "leverage"
  | "cancel_on_first_target_reached"
>;

export type ErrorsData = {
  generalErrors: ErrorTypeToDisplay;
  ordersErrors: OrdersErrors;
  existingQuantityErrors: ErrorTypeToDisplay;
  numErrors: number;
  numErrorsMessage: string;
};

export interface ExistingQuantityOrderData {
  is_existing_coins: true;
  quantity: number;
  amount: number;
  price: number;
  ratio: number;
}

export type ErrorInputTypes = keyof Pick<
  OrderData,
  "price" | "percent" | "amount"
>;

export interface useLeverageSelectorsProps {
  marginType: number;
  marginTypeOptions: Option<number>[];
  marginTypeValidationInfo?: [string, boolean];
  leverage: number;
  leverageOptions: Option<number>[];
  leverageValidationInfo?: [string, boolean];
  leverageDisabled?: boolean;
  onChange: (key: string, value: number) => void;
}

export interface CancelOnTpReachedProps {
  value: boolean | null;
  onChange: (value: boolean) => void;
  isBreakout?: boolean;
  errorMessage?: string;
}

export interface FreeTextModalProps {
  visible: boolean;
  closeModal: () => void;
}

export interface TrailingSummaryRowProps {
  orderType: OrderTypes;
}

export interface OrderProps {
  order: InputOrderData;
  index: number;
  updateOrder?: UpdateOrder;
  orderType: OrderTypes;
  amount: number;
  removeOrder?: RemoveOrder;
  orderSectionErrors?: OrderSectionErrors;
  baselineEntryPrice?: number;
  readonly?: boolean;
  columnSpans?: number[];
  ignoreRatios?: boolean;
}

export interface OrdersTableProps {
  orderType: OrderTypes;
  orders: InputOrderData[];
  useOrders?: boolean;
  ordersErrors?: OrderSectionErrors;
  resolveIssues?: () => void;
  updateOrder?: UpdateOrder;
  removeOrder?: RemoveOrder;
  baselineEntryPrice?: number;
  title?: React.JSX.Element;
  readonly?: boolean;
  spacing?: number;
  ignoreRatios?: boolean;
  /** New look: shown in the header row's action column, above the remove buttons. */
  headerAction?: React.ReactNode;
}
export interface SharedFormItemProps {
  item: ConfigurationItem;
  orderType?: OrderTypes;
  hideBorder?: boolean;
  hideTitle?: boolean;
  disabled?: boolean;
}

export interface StrategyInfo {
  numOrders: number;
  strategy: number[];
}

export interface OrdersSectionProps {
  orders: InputOrderData[];
  orderType: OrderTypes;
  setOrders: SetOrders;
  useOrders: boolean;
  setUseOrders?: (useOrders: boolean) => void;
  ordersErrors?: OrderSectionErrors;
  baselineEntryPrice: number;
  generalConfigurationData: GeneralConfigurationData;
}

export interface AddOrderButtonProps {
  addOrder: () => void;
  disabledMessage?: string;
  size?: number;
}

export interface TrailingEntryAndTakeProfitProps {
  orderType: OrderTypes;
}

export interface SelectFormItemProps {
  item: ConfigurationItem;
  orderType?: OrderTypes;
  hideBorder?: boolean;
  hideTitle?: boolean;
  disabled?: boolean;
}

export interface PriceRangeFormItemProps {
  item: ConfigurationItem;
  orderType?: OrderTypes;
}

export interface TwoSelectorsFormItemProps {
  item: ConfigurationItem;
  hideTitle?: boolean;
}

export enum ExchangeTag {
  FUTURES = "labels.futures",
  SPOT = "labels.spot",
}

export type OnChangeWithWarning = (func: () => void) => Promise<void>;
